import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ncoder/ui';
import { BudgetMeter } from '@/components/molecules/org-admin/BudgetMeter';
import { cn } from '@/lib/utils';
import type { SpendComparison, SpendSummary } from '@/services/org-admin/analysis';
import { NEAR_LIMIT_UTILISATION } from '@/services/org-admin/usageThresholds';
import { formatCurrencyUsd } from './usageFormat';

/**
 * The answer to "how exposed are we?" — designed to be readable in about two seconds.
 *
 * This panel is the fix for the Analysis view's original hierarchy problem: four equally-weighted
 * stats, two of which were engineering metrics, and no budget figure anywhere above the fold. A
 * customer who spends real money opens this page to see one number, whether it is going up, and how
 * close it is to the limit. So there is exactly one dominant figure, one delta beside it, and one
 * meter — and everything else on the page is quieter than this.
 *
 * Colour is spent only where it carries meaning: the delta and the meter. Nothing here is coloured
 * for emphasis alone.
 */

export interface SpendSummaryPanelProps {
  /** `null` while the first request is in flight. Kept nullable so the caller never has to invent a
   *  zeroed summary just to satisfy the type — a fake `$0.00` is one render away from being believed. */
  spend: SpendSummary | null;
  comparison: SpendComparison | null;
  /** Names the selected period in prose — "this month". The control above owns the selection. */
  windowLabel: string;
  isLoading?: boolean;
}

function formatPercent(value: number, digits = 0): string {
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * The clock the standing figures are on, named IN THE LABEL of every figure that uses it — the
 * module's rule (`usageFormat.ts`: a label that needs a footnote to be unambiguous is ambiguous).
 * "This cycle" is the enforced clock (backend #327 remaining); the fallback, when no row could
 * answer, is `budget − period spend`, so it is labelled with the selected period's own name.
 */
function standingClockLabel(spend: SpendSummary, windowLabel: string): string {
  return spend.standingClock === 'cycle' ? 'this cycle' : windowLabel;
}

/**
 * The period-over-period delta.
 *
 * Rising spend is not automatically bad — a growing team spends more — so this is deliberately NOT
 * painted red on any increase. It uses the warning colour only past a threshold that suggests
 * something changed rather than drifted, and the arrow plus the text carry the direction so the
 * meaning never rests on colour.
 */
function DeltaBadge({ comparison }: { comparison: SpendComparison }) {
  const { changeRatio, previousDays } = comparison;

  if (changeRatio === null) {
    return (
      <span className="oa-meta">
        {previousDays > 0
          ? `No spend in the previous ${previousDays} days`
          : 'No previous period to compare'}
      </span>
    );
  }

  const isFlat = Math.abs(changeRatio) < 0.005;
  const isUp = changeRatio > 0;
  // 25% is the point at which a change reads as a change rather than as normal variation.
  const isNotable = Math.abs(changeRatio) >= 0.25;
  const Icon = isFlat ? Minus : isUp ? ArrowUpRight : ArrowDownRight;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className={cn(
            'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[13px] font-semibold tabular-nums',
            isFlat && 'bg-muted/60 text-muted-foreground',
            !isFlat && isUp && isNotable && 'bg-warning/10 text-warning-strong',
            !isFlat && isUp && !isNotable && 'bg-muted/60 text-foreground',
            !isFlat && !isUp && 'bg-success/10 text-success-strong',
          )}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {isFlat ? 'Flat' : formatPercent(Math.abs(changeRatio))}
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[280px] text-xs">
        {formatCurrencyUsd(comparison.currentDailyAvgUsd)}/day now vs{' '}
        {formatCurrencyUsd(comparison.previousDailyAvgUsd)}/day over the previous {previousDays} days.
        Compared as daily averages because the two periods are different lengths.
      </TooltipContent>
    </Tooltip>
  );
}

/** One supporting figure. Quiet by construction — the hero above must stay dominant. */
function SubStat({
  label,
  value,
  note,
  tone = 'default',
}: {
  label: string;
  value: string;
  note?: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div className="min-w-0">
      <dt className="oa-micro truncate">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-[15px] font-semibold tabular-nums leading-none',
          tone === 'warn' ? 'text-warning-strong' : 'text-foreground',
        )}
      >
        {value}
      </dd>
      {note && <p className="oa-meta mt-1 truncate">{note}</p>}
    </div>
  );
}

/**
 * Budget consumption as a meter rather than a chart.
 *
 * A single ratio against a limit is the textbook case for a meter: one bar, one track, one threshold.
 * A two-slice pie or a one-bar bar chart carries the same information with more ink and less clarity.
 *
 * **The track always represents the budget.** The previous version rescaled it when over budget — the
 * full track meant "the budget" under the limit and "actual spend" over it, with no marker to say
 * which. Two different scales wearing the same bar is exactly the kind of quiet dishonesty that makes
 * a reader stop trusting a dashboard, and at 300% over it looked barely different from 110% over.
 * Now 100% of the track is always the budget, the fill is capped there, and the overage is stated as
 * a number — which is both scale-honest and easier to act on than a proportional sliver.
 */
function BudgetStanding({ spend, windowLabel }: { spend: SpendSummary; windowLabel: string }) {
  /*
   * The meter borrows the CHART palette, not `--primary`.
   *
   * The studio shell deliberately remaps `--primary` to a near-black charcoal, which is right for
   * buttons and wrong here: it rendered the meter as a heavy black slab that read as decoration
   * rather than as a measured quantity. Using the same blue as the bars below also ties the meter to
   * the charts it summarises, so the page has one colour language for "amount of money".
   */
  if (spend.utilisation === null) {
    return (
      <p className="oa-meta">
        No seat budgets are set, so there is no limit to measure against. Budgets are set per member
        on the Usage tab.
      </p>
    );
  }

  const utilisation = spend.utilisation;
  const isOver = utilisation >= 1;
  const isNearLimit = !isOver && utilisation >= NEAR_LIMIT_UTILISATION;
  const overBy = Math.max(0, spend.standingSpentUsd - spend.budgetUsd);
  const clockLabel = standingClockLabel(spend, windowLabel);

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="text-[13px]">
          <span
            className={cn(
              'font-semibold tabular-nums',
              isOver && 'text-destructive-strong',
              isNearLimit && 'text-warning-strong',
            )}
          >
            {formatPercent(utilisation)}
          </span>{' '}
          <span className="text-muted-foreground">
            of {formatCurrencyUsd(spend.budgetUsd)} allowance used {clockLabel}
          </span>
          {isOver && (
            <span className="font-medium text-destructive-strong">
              {' '}
              · {formatCurrencyUsd(overBy)} over
            </span>
          )}
        </p>
        {spend.daysLeftInCycle !== null && (
          <p className="oa-meta tabular-nums">
            {spend.daysLeftInCycle === 0
              ? 'Cycle resets today'
              : `${spend.daysLeftInCycle} ${spend.daysLeftInCycle === 1 ? 'day' : 'days'} left in cycle`}
          </p>
        )}
      </div>

      {/* The shared meter — the same component the per-member table uses, so the two can never
          disagree about where amber starts or how the label is worded. */}
      <BudgetMeter
        className="mt-2"
        spentUsd={spend.standingSpentUsd}
        budgetUsd={spend.budgetUsd}
        label="This organization"
      />
    </div>
  );
}

/**
 * The one sentence that turns four figures into a decision.
 *
 * Burn rate, days remaining, remaining budget and the projection are individually inert — the reader
 * had to hover the delta badge for the daily rate and then do arithmetic against days-left to learn
 * whether they were going to make it. This states the conclusion, which is the actual question:
 * *will this cycle land inside the budget or not, and by how much?*
 */
function RunwayNote({ spend }: { spend: SpendSummary }) {
  const { dailyBurnUsd, projectedCycleUsd, budgetUsd, utilisation } = spend;

  // Already over: a projection is beside the point, the overage has happened.
  if (utilisation !== null && utilisation >= 1) {
    return (
      <p className="mt-2.5 text-[13px] text-muted-foreground">
        This cycle is already{' '}
        <span className="font-medium text-destructive-strong">
          {formatCurrencyUsd(spend.standingSpentUsd - budgetUsd)} over
        </span>{' '}
        its {formatCurrencyUsd(budgetUsd)} allowance.
      </p>
    );
  }

  if (dailyBurnUsd === null || projectedCycleUsd === null) {
    return (
      <p className="mt-2.5 oa-meta">
        {spend.windowUsd === 0
          ? 'No spend recorded yet.'
          : 'Too early in the cycle to project an end-of-period total.'}
      </p>
    );
  }

  const gap = projectedCycleUsd - budgetUsd;

  return (
    <p className="mt-2.5 text-[13px] text-muted-foreground">
      Spending{' '}
      <span className="font-medium tabular-nums text-foreground">
        {formatCurrencyUsd(dailyBurnUsd)}/day
      </span>
      {budgetUsd > 0 ? (
        <>
          {' '}
          — on track to finish around{' '}
          <span
            className={cn(
              'font-medium tabular-nums',
              gap > 0 ? 'text-warning-strong' : 'text-foreground',
            )}
          >
            {formatCurrencyUsd(projectedCycleUsd)}
          </span>
          {gap > 0 ? (
            <>
              , <span className="font-medium text-warning-strong">{formatCurrencyUsd(gap)} over</span>{' '}
              budget.
            </>
          ) : (
            <>
              , {formatCurrencyUsd(Math.abs(gap))} inside budget.
            </>
          )}
        </>
      ) : (
        <>
          {' '}
          — around{' '}
          <span className="font-medium tabular-nums text-foreground">
            {formatCurrencyUsd(projectedCycleUsd)}
          </span>{' '}
          by the end of this cycle.
        </>
      )}
    </p>
  );
}

export function SpendSummaryPanel({
  spend,
  comparison,
  windowLabel,
  isLoading,
}: SpendSummaryPanelProps) {
  if (isLoading || !spend) {
    return (
      <section className="oa-panel p-[var(--oa-gutter)]" aria-busy="true" aria-label="Loading spend summary">
        <div className="oa-skeleton h-2.5 w-28" />
        <div className="oa-skeleton mt-3 h-9 w-52" />
        <div className="oa-skeleton mt-4 h-2 w-full rounded-full" />
        <div className="mt-5 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(120px,1fr))]">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index}>
              <div className="oa-skeleton h-2.5 w-16" />
              <div className="oa-skeleton mt-2 h-3.5 w-20" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  /*
   * Whether a real earlier period exists to compare against.
   *
   * The API returns a cumulative total for one named period and no history, so a previous period has
   * to be derived by subtracting nested day ranges. Calendar months do not nest, so since this tab
   * moved to months (`useTokenUsageWindow`) the adapter returns `comparison: null` outright and the
   * delta is stated as absent rather than shown as a dash. `suggestedWindow` survives for the days
   * ranges the adapter still supports elsewhere — it is the adapter saying "not here, but there".
   */
  const canCompare = comparison !== null && comparison.suggestedWindow === undefined;
  const clockLabel = standingClockLabel(spend, windowLabel);

  return (
    <section className="oa-panel" aria-label="Spend summary">
      <div className="p-[var(--oa-gutter)]">
        {/* THE number, and now the ONLY one on this card — the selected reporting period.
            It was labelled "Spend · this cycle" while a sub-stat below showed the period figure, so
            the card carried two numbers ($1.578 over $1.5585 on dev, 2026-09-11) under two clocks.
            The period is what the control above this card selects and what every other tile on the
            tab reports, so the label names that and the sub-stat is gone. */}
        <h2 className="oa-micro">{`Spend · ${windowLabel}`}</h2>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="text-[34px] font-semibold leading-none tracking-[-0.02em] tabular-nums">
            {formatCurrencyUsd(spend.windowUsd)}
          </p>
          {canCompare ? (
            <DeltaBadge comparison={comparison} />
          ) : (
            /*
             * THE COMPARE BUTTON IS GONE, AND SHOULD STAY GONE.
             *
             * There used to be a button here offering to switch to the 15-day view, where a
             * comparison is derivable. It was removed because a comparison control must never
             * NAVIGATE: pressing it rewrote the reporting range, which is shared with the rest of
             * this screen through the URL, so every figure in view changed at once. Its original
             * label — "Compare periods" — promised a comparison view and delivered a filter change,
             * and even after being relabelled with its effect it was still a cross-screen side
             * effect triggered from inside one card.
             *
             * An absence honestly explained beats a control that does something else. When the API
             * grows arbitrary date ranges, comparison belongs INSIDE the range control as a
             * "Compare to" option — which is where every mature analytics product puts it, because
             * comparison is a property of the range.
             *
             * The copy says "not compared", not "no earlier period exists". The first is true; the
             * second is a claim about data this tab does not fetch in month mode.
             */
            <span className="oa-meta">Not compared to an earlier period</span>
          )}
        </div>
        {canCompare && (
          <p className="oa-meta mt-2">
            Daily spend rate vs the previous {comparison.previousDays} days
          </p>
        )}

        <div className="mt-4">
          <BudgetStanding spend={spend} windowLabel={windowLabel} />
          <RunwayNote spend={spend} />
        </div>
      </div>

      {/*
        Supporting figures, visually subordinate: smaller type, on a tinted rail, below the hero. This
        is the hierarchy that was missing — previously every figure competed at the same weight.
      */}
      <dl className="grid gap-x-4 gap-y-4 border-t border-border/50 bg-muted/20 p-[var(--oa-gutter)] [grid-template-columns:repeat(auto-fit,minmax(104px,1fr))]">
        {/* Labels are kept short enough to fit the narrowest column without truncating — the period
            is already named by the segmented control directly above, so repeating "last 30 days" in
            full here bought nothing and cost the label its last word on a phone. */}
        {/*
          The "Spend + automation" sub-stat that used to sit here is GONE, and deliberately.

          It rendered `windowUsd + automationWindowUsd`, but the customer-facing report has carried
          no `automation` block since it moved to the events source — `UsageContext.events_report/8`
          says so outright ("this customer-facing report carries no `automation` block: org-level
          spend with no seat is internal infra cost, not the customer's to see"). So the term was
          always `+ 0`, and the tile was a second rendering of the hero under a name that promised
          data the payload cannot contain.

          Worse, the two disagreed: the hero read cycle spend and this read window spend, so the
          same card showed "$1.578" above "$1.5585" (dev, 2026-09-11) with the smaller number
          labelled as the one that included MORE. Both now come from one figure, so repeating it
          here would say nothing.
        */}
        <SubStat
          label={`Allowance · ${clockLabel}`}
          value={spend.budgetUsd > 0 ? formatCurrencyUsd(spend.budgetUsd) : '—'}
          note={
            spend.budgetUsd <= 0
              ? 'none set'
              : spend.planBudgetUsd !== null && spend.creditBalanceUsd !== null
                ? `plans ${formatCurrencyUsd(spend.planBudgetUsd)} + credits ${formatCurrencyUsd(spend.creditBalanceUsd)}`
                : 'all seats'
          }
        />
        <SubStat
          label={`Available · ${clockLabel}`}
          value={spend.budgetUsd > 0 ? formatCurrencyUsd(spend.remainingUsd) : '—'}
          /* The clock is in the label (house rule); the note carries the one thing a reader cannot
             guess — that the credit part of what is left does not vanish at the reset. */
          note={
            spend.budgetUsd > 0 && spend.creditRemainingUsd !== null
              ? `credits ${formatCurrencyUsd(spend.creditRemainingUsd)} carry over`
              : undefined
          }
          tone={spend.budgetUsd > 0 && spend.remainingUsd === 0 ? 'warn' : 'default'}
        />
        {/*
          Burn rate, not the projection.
          The projection now lives in the runway sentence under the meter, where it sits next to the
          days remaining and the budget that make it mean something — a bare "$2,018" in a tile is a
          number the reader still has to do work on. Burn rate is the figure that belongs here: it is
          the org's spend velocity, it needs no context to be understood, and it is what an admin
          quotes when they ask a team to slow down.
        */}
        <SubStat
          label="Daily burn"
          value={spend.dailyBurnUsd === null ? '—' : formatCurrencyUsd(spend.dailyBurnUsd)}
          note={
            spend.dailyBurnUsd === null
              ? spend.windowUsd === 0
                ? 'no spend yet'
                : 'too early in cycle'
              : 'this cycle, per day'
          }
          tone={spend.projectedOverBudget ? 'warn' : 'default'}
        />
      </dl>
    </section>
  );
}
